import {
  Component,
  computed,
  contentChildren,
  effect,
  input,
  signal,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  ElementRef,
  viewChild,
  output,
  AfterViewInit,
} from '@angular/core';
import emblaCarouselWrapper, { type EmblaCarouselType, type EmblaOptionsType } from 'embla-carousel';
import type { ClassValue } from 'clsx';
import { mergeClasses } from '../../../../utils/merge-classes';
import { carouselContentVariants, carouselItemVariants } from './carousel.variants';

@Component({
  selector: 'z-carousel',
  standalone: true,
  template: `
    <div class="relative" (wheel)="onWheel($event)" #carouselContainer>
      <div class="overflow-hidden" #viewportRef>
        <ng-content></ng-content>
      </div>
      @if (canScrollPrev() || canScrollNext()) {
        <div class="absolute -left-12 -right-12 top-0 flex h-full items-center justify-between pointer-events-none">
          <button
            type="button"
            class="h-8 w-8 rounded-full border bg-white shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed pointer-events-auto transition-colors"
            [disabled]="!canScrollPrev()"
            (click)="scrollPrev()"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mx-auto">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
          <button
            type="button"
            class="h-8 w-8 rounded-full border bg-white shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed pointer-events-auto transition-colors"
            [disabled]="!canScrollNext()"
            (click)="scrollNext()"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mx-auto">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>
      }
    </div>
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'relative w-full',
    '[attr.role]': '"region"',
    '[attr.aria-roledescription]': '"carousel"',
  },
})
export class ZardCarouselComponent implements AfterViewInit {
  private viewportRef = viewChild.required<ElementRef<HTMLDivElement>>('viewportRef');
  private carouselContainer = viewChild<ElementRef<HTMLDivElement>>('carouselContainer');

  readonly zOptions = input<EmblaOptionsType>({});
  readonly zPlugins = input<any[]>([]);
  readonly zEnableWheelScroll = input<boolean>(true);

  readonly onSlideChange = output<number>();

  protected readonly canScrollPrev = signal(false);
  protected readonly canScrollNext = signal(false);
  protected readonly selectedIndex = signal(0);

  private emblaApi: EmblaCarouselType | null = null;
  private scrollTimeout: any = null;
  private isScrolling = false;
  private scrollThreshold = 30; // Minimum scroll delta to trigger navigation

  ngAfterViewInit() {
    this.initCarousel();
  }

  private initCarousel() {
    const viewport = this.viewportRef().nativeElement;
    const options = this.zOptions();
    const plugins = this.zPlugins();

    this.emblaApi = emblaCarouselWrapper(viewport, options, plugins);

    this.emblaApi.on('select', () => {
      this.updateState();
    });

    this.emblaApi.on('reInit', () => {
      this.updateState();
    });

    this.updateState();
  }

  private updateState() {
    if (!this.emblaApi) return;

    this.canScrollPrev.set(this.emblaApi.canScrollPrev());
    this.canScrollNext.set(this.emblaApi.canScrollNext());
    this.selectedIndex.set(this.emblaApi.selectedScrollSnap());
    this.onSlideChange.emit(this.selectedIndex());
  }

  scrollPrev() {
    this.emblaApi?.scrollPrev();
  }

  scrollNext() {
    this.emblaApi?.scrollNext();
  }

  scrollTo(index: number) {
    this.emblaApi?.scrollTo(index);
  }

  onWheel(event: WheelEvent) {
    // if (!this.zEnableWheelScroll() || this.isScrolling) return;

    // const deltaX = event.deltaX;
    // const deltaY = event.deltaY;
    // const absDeltaX = Math.abs(deltaX);
    // const absDeltaY = Math.abs(deltaY);

    // // Determine primary scroll direction
    // const isHorizontalScroll = absDeltaX > absDeltaY;
    // const isVerticalScroll = absDeltaY > absDeltaX;

    // // Check if scroll exceeds threshold (use lower threshold for horizontal)
    // const horizontalThreshold = 10; // More sensitive for horizontal
    // const verticalThreshold = this.scrollThreshold;

    // const meetsThreshold = (isHorizontalScroll && absDeltaX > horizontalThreshold) ||
    //   (isVerticalScroll && absDeltaY > verticalThreshold);

    // if (!meetsThreshold) return;

    // // Determine scroll direction
    // let shouldScrollNext = false;
    // let shouldScrollPrev = false;

    // if (isHorizontalScroll) {
    //   shouldScrollNext = deltaX > 0;
    //   shouldScrollPrev = deltaX < 0;
    // } else if (isVerticalScroll) {
    //   shouldScrollNext = deltaY > 0;
    //   shouldScrollPrev = deltaY < 0;
    // }

    // // Perform navigation if possible
    // const canNavigate = (shouldScrollNext && this.canScrollNext()) ||
    //   (shouldScrollPrev && this.canScrollPrev());

    // if (canNavigate) {
    //   // Prevent default browser behavior
    //   event.preventDefault();
    //   event.stopPropagation();

    //   // Set scrolling flag to prevent rapid-fire
    //   this.isScrolling = true;

    //   // Navigate carousel
    //   if (shouldScrollNext) {
    //     this.scrollNext();
    //   } else if (shouldScrollPrev) {
    //     this.scrollPrev();
    //   }

    //   // Reset scrolling flag after animation completes
    //   if (this.scrollTimeout) {
    //     clearTimeout(this.scrollTimeout);
    //   }
    //   this.scrollTimeout = setTimeout(() => {
    //     this.isScrolling = false;
    //   }, 300);
    // }
  }
}

@Component({
  selector: 'z-carousel-content',
  standalone: true,
  template: `<ng-content></ng-content>`,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'classes()',
  },
})
export class ZardCarouselContentComponent {
  readonly class = input<ClassValue>('');

  protected readonly classes = computed(() =>
    mergeClasses(carouselContentVariants(), this.class())
  );
}

@Component({
  selector: 'z-carousel-item',
  standalone: true,
  template: `<ng-content></ng-content>`,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'classes()',
    '[attr.role]': '"group"',
    '[attr.aria-roledescription]': '"slide"',
  },
})
export class ZardCarouselItemComponent {
  readonly class = input<ClassValue>('');

  protected readonly classes = computed(() =>
    mergeClasses(carouselItemVariants(), this.class())
  );
}
